import React from 'react'
import normalizeText from './normalizeText'
import { NO_EXECUTE_CONTENTFULL_ROUTES } from './utils'
import type {
	AskResponse,
	ForTodayResponse,
	MetricsResponse,
	PlanResponse,
	ParQResponse,
	AskOption,
	AskItem,
	BeneficioItem,
	RichText
} from '../types/contentful.types'

import type {
	ProcessedAskData,
	ProcessedForTodayData,
	ProcessedMetricsData,
	ProcessedPlanItem,
	ProcessedParQData,
	ContentfulFoodFrequency,
	ProcessedForTodayBenefit
} from '../types/processed.types'

// Transformadores para Ask (Preguntas)
const transformAskOptions = ({
	data,
	isYesNo = null,
	type,
	foodFrequency
}: {
	data: AskOption[]
	isYesNo?: boolean | null
	type: string
	foodFrequency: ContentfulFoodFrequency[]
}): any[] => {
	if (!data || !Array.isArray(data)) return []

	const template = {
		isYesNo: data.map(item => ({
			value: item.respuesta === 'Si',
			label: item.respuesta
		})),
		normal: data.map(item => ({
			value: item?.respuesta?.includes('Hipertensión arterial')
				? item.respuesta.split(' ')?.[0] || item.respuesta
				: item.respuesta,
			label: item.respuesta
		})),
		imagenmulticheck: data.map(item => {
			const frequencyASk = foodFrequency.map(e => ({
				value: e.respuesta,
				label: e.respuesta
			}))
			return {
				imagen: item?.imagenCollection?.items?.[0]?.url || '',
				titulo: item.respuesta,
				texto: item?.textoInput || '',
				preguntas: frequencyASk?.length > 0 ? frequencyASk : []
			}
		}),
		imagenes: data
			.map(item => ({
				value: item.respuesta,
				label: item.respuesta,
				img: item?.imagenCollection?.items?.[0]?.url,
				imgActive: item?.imagenActiva?.url
			}))
			.filter(e => e.value !== null)
	}

	return template[type as keyof typeof template] || template[isYesNo ? 'isYesNo' : 'normal']
}

export const transformAskData = (data: AskResponse): ProcessedAskData => {
	if (
		!data?.homeCollection?.items?.[0]?.referenciaPreguntasYClases?.preguntasHomeBasecareCollection
			?.items
	) {
		return {}
	}

	const askItems =
		data.homeCollection.items[0].referenciaPreguntasYClases.preguntasHomeBasecareCollection.items
	const mappedAsk: ProcessedAskData = {}

	askItems.forEach((ask: AskItem) => {
		const hasOptions = ask?.referenciaOpcionesCollection?.items
		const isYesNo = hasOptions && hasOptions.length > 0 && hasOptions[0]?.respuesta === 'Si'

		mappedAsk[normalizeText(ask?.titulo)] = {
			id: parseInt(ask?.idTimify) || 0,
			tipo: ask?.tipo || '',
			accion: ask?.accion || '',
			nombre: ask?.nombre || '',
			pregunta: ask?.pregunta || '',
			opciones: transformAskOptions({
				data: hasOptions || [],
				isYesNo,
				type: ask?.tipo || 'normal',
				foodFrequency: ask?.frecuenciaDeAlimentosCollection?.items || []
			}),
			isYesNo: isYesNo || null,
			extra: ask?.preguntaExtra
				? {
						texto: ask.preguntaExtra,
						tipo: ask?.tipoPreguntaExtra || '',
						textoInput: ask?.textoInput
				  }
				: undefined
		}
	})

	return mappedAsk
}

// Transformadores para ForToday
export const transformForTodayData = (data: ForTodayResponse): ProcessedForTodayData => {
	if (!data?.homeCollection?.items?.[0]?.vistas?.tuRutaDeCuidadoParaHoy) {
		return {
			medicineTemplate: false,
			planCareContent: false,
			headForToday: {},
			contForToday: {}
		}
	}

	const forTodayData = data.homeCollection.items[0].vistas.tuRutaDeCuidadoParaHoy
	const mappedForToday: { [key: string]: ProcessedForTodayBenefit[] } = {}
	let headSorted: { tituloGeneral?: string; headButtonsText?: string[] } = {}
	let newMedicineTemplate: BeneficioItem | false = false
	let newPlanCareContent: (BeneficioItem & { icon?: any }) | false = false

	Object.entries(forTodayData).forEach(([key, value]) => {
		if (key === 'tituloGeneral' && typeof value === 'string') {
			headSorted = {
				...headSorted,
				[key]: value
			}
		}

		if (key === 'beneficioCollection' && value && typeof value === 'object' && 'items' in value) {
			const benefits = (value as { items: BeneficioItem[] }).items
			newMedicineTemplate = benefits?.[0] || false
			newPlanCareContent = benefits?.[1]
				? {
						...benefits[1],
						icon: benefits[1]?.beneficioCollection?.items?.[0]?.icono
				  }
				: false
		}

		const exceptionsList = ['__typename', 'tituloGeneral', 'beneficioCollection']

		if (
			!exceptionsList.includes(key) &&
			value &&
			typeof value === 'object' &&
			'tituloGeneral' in value
		) {
			const section = value as {
				tituloGeneral: string
				beneficioCollection: { items: BeneficioItem[] }
			}

			headSorted = {
				...headSorted,
				headButtonsText: headSorted?.headButtonsText
					? [...headSorted.headButtonsText, section.tituloGeneral]
					: [section.tituloGeneral]
			}

			mappedForToday[normalizeText(section.tituloGeneral)] = section.beneficioCollection.items.map(
				e => ({
					...e,
					id: e.idReto,
					titulo: e.tituloGeneral,
					descripcion: e.descripcionGeneral,
					imagen: e.imagen?.url,
					link: e.link,
					tipo: e.tipoDeReto,
					time: e?.subtituloVideo || null
				})
			)
		}
	})

	return {
		medicineTemplate: newMedicineTemplate,
		planCareContent: newPlanCareContent,
		headForToday: headSorted,
		contForToday: mappedForToday
	}
}

// Transformadores para Metrics
export const transformMetricsData = (data: MetricsResponse): ProcessedMetricsData => {
	if (!data?.homeCollection?.items?.[0]) {
		return {
			trackingList: [],
			campaignList: []
		}
	}

	const metricsData = data.homeCollection.items[0]

	return {
		trackingList: metricsData?.placeholdersBuscador || [],
		campaignList: metricsData?.listaSdk || []
	}
}

// Transformadores para Plan
export const transformPlanData = (data: PlanResponse): ProcessedPlanItem[] => {
	if (
		!data?.homeCollection?.items?.[0]?.referenciaRetos?.retosSaludContinuaFrontCollection?.items
	) {
		return []
	}

	const planItems =
		data.homeCollection.items[0].referenciaRetos.retosSaludContinuaFrontCollection.items

	return planItems.map((plan: any) => ({
		...plan,
		challengeId: parseInt(plan?.semana) || 0,
		redTitle: plan?.numeroDelReto || '',
		challenge: plan?.referenciaSemanasCollection?.items?.[0]
	}))
}

// Utilidades para Plan
export const getPlanTextFromRichText = (text?: RichText): string => {
	return text?.json?.content?.[0]?.content?.[0]?.value || ''
}

export const getCompletePlanTextFromRichText = (text?: RichText): JSX.Element[] => {
	const data = text?.json?.content?.[0]?.content ?? []
	return data.map((e: any, i: number) => React.createElement('p', { key: i }, e.value))
}

export const filterPlanByChallenge = ({
	id,
	data
}: {
	id: number
	data: ProcessedPlanItem[]
}): ProcessedPlanItem | undefined => {
	return data?.find(content => content?.challengeId === id)
}

// Transformadores para ParQ
export const transformParQData = (data: ParQResponse): ProcessedParQData => {
	if (!data?.homeCollection?.items?.[0]?.parQ) {
		return {
			contParQ: false,
			parQQuestions: false,
			parQModal: false
		}
	}

	const parQData = data.homeCollection.items[0].parQ
	let contParQ: any = false
	let parQQuestions: any = false
	let parQModal: any = false

	// Transformar contenido ParQ
	if (parQData?.parQContent) {
		const content = parQData.parQContent
		contParQ = {
			title: content?.titulo || '',
			img: {
				src: content?.imagen?.url,
				alt: content?.imagen?.title
			},
			text: content?.descripcion || '',
			'button-title': content?.tituloBoton || ''
		}

		// Transformar modales
		const modals: { [key: string]: any } = {}
		content?.modalesCollection?.items?.forEach((e: any) => {
			modals[e.nombreModal] = {
				title: e?.titulo || '',
				description: e?.descripcion || '',
				button: e?.tituloBoton || '',
				img: e?.imagen,
				linkBoton: e?.linkBoton
			}
		})
		parQModal = modals
	}

	// Transformar preguntas ParQ
	if (parQData?.parQPreguntas) {
		const questions = parQData.parQPreguntas
		parQQuestions = {
			'texto-observacion': questions?.textoObservacion || '',
			'nombre-boton': questions?.tituloBoton || '',
			preguntas:
				questions?.preguntasCollection?.items?.map((e: any) => ({
					texto: e.descripcion,
					respuesta: [
						{ value: 'Si', label: 'Si' },
						{ value: 'No', label: 'No' }
					]
				})) || []
		}
	}

	return {
		contParQ,
		parQQuestions,
		parQModal
	}
}

// Función utilitaria para verificar si se debe ejecutar la query (para métricas)
export const shouldExecuteContentfulQuery = (pathname: string): boolean => {
	return !NO_EXECUTE_CONTENTFULL_ROUTES.includes(pathname)
}
